import React, { useEffect, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";

import { getUserSavedSearch } from "@/api/savedSearch";
import { deleteSavedSearchPreference } from "@/api/savedPreference";
import { setIsAddedToSaved } from "@/redux/savedpreference/savedPreferenceSlice";
import { ShimmerContentBlock } from "react-shimmer-effects";
import { formDescription, formUrl } from "@/components/srp/searchParams";
import {
  getEventCategory,
  trackApiCall,
  trackButtonClick,
} from "@/utils/analytics";
import { markInternalNavigation } from "@/utils/attributionCapture";

const SavedSearch = () => {
  const user = useSelector((state) => state.user);
  const [searchData, setSearchData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const initialFetchDone = useRef(false);
  const resultsPerPage = 3;

  // Fetch saved searches based on currentPage
  const getSearchData = useCallback(
    async (page, size) => {
      try {
        setLoading(true);
        const data = await getUserSavedSearch(
          user.user.userEmailId,
          (page - 1) * size, // Adjust offset calculation
          size
        );

        if (data.saveSearchResponseList) {
          setSearchData((prevSearchData) => [
            ...prevSearchData,
            ...data.saveSearchResponseList,
          ]);

          // Improved hasMore logic
          setHasMore(data.saveSearchCount > page * size);
        } else {
          setSearchData([]);
          setHasMore(false);
        }
      } catch (error) {
        console.error("Error fetching saved searches:", error);
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    },
    [user.user.userEmailId, resultsPerPage]
  );

  const deleteSavedSearch = async (id) => {
    let eventCategory = getEventCategory();

    trackButtonClick({
      eventName: "delete_save_search",
      eventCategory: `${eventCategory} - Save List Slider`,
      eventSource: "Delete save search button click",
      eventLabel: `Search Id : ${id}`,
    });

    try {
      const apiData = { emailId: user.user.userEmailId, searchId: id };
      const data = await deleteSavedSearchPreference(apiData);
      if (data.response === "SUCCESS") {
        trackApiCall({
          eventName: "delete_save_search_success",
          eventCategory: `${eventCategory} - Save List Slider`,
          eventSource: "Delete save search api call",
          eventLabel: `Search Id : ${id}`,
        });

        setSearchData([]); // Clear the existing search data
        setCurrentPage(1); // Reset to the first page
        // setHasMore(true); // Reset hasMore flag
        getSearchData(1, resultsPerPage);
        dispatch(setIsAddedToSaved());
      }
    } catch (error) {
      console.log(error);
    }
  };

  // Initial load - should run only once
  useEffect(() => {
    if (!initialFetchDone.current) {
      getSearchData(1, resultsPerPage);
      initialFetchDone.current = true;
    }
  }, [getSearchData, currentPage]);

  // Load more handler
  const handleLoadMore = () => {
    if (!loading && hasMore) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      getSearchData(nextPage, resultsPerPage);
    }
  };

  return (
    <div>
      {loading && (
        <div className="mt-4">
          {[1, 2, 3].map((item) => (
            <div key={item} className="w-100 mb-3">
              <ShimmerContentBlock
                cta
                card
                thumbnailWidth={130}
                thumbnailHeight={100}
              />
            </div>
          ))}
        </div>
      )}

      {searchData.length === 0 && !loading && (
        <h3 className="no-data">No saved searches available</h3>
      )}

      {searchData.length > 0 &&
        !loading &&
        searchData.map((data) => (
          <div className="saved-search" key={data.saveSearchId}>
            <h4 className="saved-search-header">{data.searchName}</h4>
            <div className="saved-search-description">
              {formDescription(data)}
            </div>
            <div className="search-buttons">
              <a id={`saved-search-view-${data.saveSearchId}`} className="btn booking_btn float-end card-btn-thm" href={formUrl(data)} onClick={() => markInternalNavigation()}>
                View Results
              </a>
              <button
                id={`saved-search-delete-${data.saveSearchId}`}
                className="btn btn-sm delete-search"
                onClick={() => deleteSavedSearch(data.saveSearchId)}
              >
                Delete
              </button>
            </div>
          </div>
        ))}

      {hasMore && !loading && searchData.length > 0 && (
        <div className="d-flex justify-content-center">
          <button id="saved-search-load-more-btn" onClick={handleLoadMore} className="btn hubzu-btn-thm">
            Show more
          </button>
        </div>
      )}
    </div>
  );
};

export default SavedSearch;
